import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { http } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Lote 90 — mensagens internas: contador de não lidas (menu) + pop-up quando chega mensagem nova
// com o app aberto. Consulta o servidor a cada ~20 s enquanto a tela está visível (sem WebSocket:
// mais simples e sem custo extra). Com o app fechado quem avisa é o push (sem o texto da mensagem).
const MensagensContext = createContext({ unread: 0, convites: 0, ultima: null, refresh: () => {} });
export const useMensagens = () => useContext(MensagensContext);

const POLL_MS = 20000;

export function MensagensProvider({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ unread: 0, convites: 0, ultima: null });
  const since = useRef(null);

  const poll = useCallback(async () => {
    if (!user || document.visibilityState === "hidden") return;
    try {
      const r = await http.get("/msg/poll", { params: since.current ? { since: since.current } : {} });
      const d = r.data;
      since.current = d.agora;
      setState({ unread: d.unread || 0, convites: d.convites || 0, ultima: d.ultima || null });
      const aberta = new URLSearchParams(window.location.search).get("t");
      const naPagina = window.location.pathname === "/mensagens";
      (d.novas || []).forEach((m) => {
        if (naPagina && aberta === m.thread_id && document.visibilityState === "visible") return; // já está lendo
        toast(m.group ? `${m.sender_name} em ${m.group}` : `Mensagem de ${m.sender_name}`, {
          id: `msg-${m.thread_id}`,
          description: m.preview,
          duration: 9000,
          action: { label: "Abrir", onClick: () => navigate(`/mensagens?t=${m.thread_id}`) },
        });
      });
    } catch {
      /* sem internet ou servidor reiniciando: tenta de novo no próximo ciclo */
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return undefined;
    poll();
    const id = setInterval(poll, POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", poll);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", onVis); window.removeEventListener("focus", poll); };
  }, [user, poll]);

  return (
    <MensagensContext.Provider value={{ ...state, refresh: poll }}>
      {children}
    </MensagensContext.Provider>
  );
}
